/**
 * @fileoverview The share picture's choices: its sizes and the edges an app
 * covers, its layouts, the card's grounds, what may be printed, the lettering's
 * steps, and what buildShareFrames is given (ShareFrameInput). Moved out of
 * plant-list/lib/shareFrames (batch 18, A110); nothing in it changed.
 *
 * Where: the share studio (features/share/studio), the frames' drawing
 * (features/share/frames), and through plant-list/lib/shareFrames the share
 * style, the share draft, the season's picture and the shares API's types.
 * Settings: SHARE_FRAME_SIZES, SHARE_STORY_SAFE_PX, SHARE_TEXT_SCALES and
 * SHARE_TEXT_BASE (config/tunables/plantList); Grow settings sharing.GROUNDS.
 *
 * @module frameOptions
 */

import type { Subject } from '@/shared/api/subject';
import type { PlantPhoto } from '@/shared/types/photo';
import type { LinkKey } from '@/shared/lib/socialLinks';
import { growSetting, type GroundColours } from '@/shared/lib/growSettings';
import { SHARE_FRAME_SIZES, SHARE_STORY_SAFE_PX, SHARE_TEXT_BASE, SHARE_TEXT_SCALES } from '@/config/tunables/plantList';

/** Each size's picture in pixels, by its name; set in the tunables. */
export const SHARE_SIZES = SHARE_FRAME_SIZES;

/** A frame's size, by its name: square, portrait, story or pin. */
export type ShareSize = keyof typeof SHARE_SIZES;
/** The sizes, in the order the sharer offers them. */
export const SHARE_SIZE_VALUES = ['square', 'portrait', 'story', 'pin'] as const;

/**
 * The edges of a frame that the app showing it will cover.
 *
 * A story is not shown as a picture; it is shown as a screen with the app's
 * own furniture on top. Instagram puts the account and the close button over
 * the first 250 pixels of a 1920-tall story and the reply bar over the last
 * 250, and Reels and TikTok take at least as much. Lettering placed against
 * the bottom edge, which is where it belongs on a photograph, ends up
 * underneath that bar — it was never cropped, it was covered.
 *
 * Held as a fraction of the height so it survives a change of size, and
 * exported so the preview can draw the same boundary the frame respects.
 * A square and a Pinterest pin are shown as pictures, with nothing over them.
 */
export const SHARE_SAFE: Record<ShareSize, { top: number; bottom: number }> = {
  square: { top: 0, bottom: 0 },
  portrait: { top: 0, bottom: 0 },
  story: { top: SHARE_STORY_SAFE_PX / SHARE_FRAME_SIZES.story.h, bottom: SHARE_STORY_SAFE_PX / SHARE_FRAME_SIZES.story.h },
  pin: { top: 0, bottom: 0 },
};

/**
 * How the chosen photographs are arranged.
 *
 *   full — every chosen photograph at its own full size. One file each.
 *   card — each one inside a tilted card on a plain ground, the lettering
 *          under it. One file each.
 *   peek — each one, with a sliver of the next at its edge. One file each.
 *   ba   — the first and the last side by side, with the days between them.
 *          One file.
 *   collage — the chosen photographs laid out together on a plain border,
 *          no words on any photograph, a small credit inside the corner
 *          one (SHARE_COLLAGE); then each photograph on its own. One file
 *          more than the photographs, so a post becomes a carousel.
 */
export const SHARE_LAYOUT_VALUES = ['collage', 'full', 'card', 'peek', 'ba'] as const;

/** What a card sits on when the app has never heard the Grow setting sharing.GROUNDS: plain, and not the owner's. */
const UNHEARD_GROUND: GroundColours = { fill: '#2B2B2B', ink: '#FFFFFF' };

/**
 * The grounds a card can sit on, by the names gardeners' saved choices keep.
 * Garden green first, the one it always had. Each one's colour, and the ink
 * the Grow mark and handle take on it, are the Grow setting sharing.GROUNDS,
 * changed in the backend's Grow Settings panel; see groundColours.
 */
export const CARD_GROUND_VALUES = ['green', 'cream', 'terracotta', 'sky', 'charcoal', 'blush'] as const;
/** A card ground's name. */
export type CardGround = (typeof CARD_GROUND_VALUES)[number];
/** A layout's name: collage, full, card, peek, or ba (before and after). */
export type ShareLayout = (typeof SHARE_LAYOUT_VALUES)[number];

/**
 * A card ground's colour and its ink, as the Grow settings have them.
 *
 * @param ground - The ground's name.
 * @returns "#RRGGBB" colours; green's if the name is not one the settings know, and a plain dark card
 *   if the app has never heard the grounds at all (a first visit that could not reach the server).
 */
export function groundColours(ground: CardGround): GroundColours {
  const grounds = growSetting('sharing.GROUNDS');
  return grounds?.[ground] ?? grounds?.green ?? UNHEARD_GROUND;
}

/** What may be printed on the picture. The address is not in here. */
export interface ShareElements {
  /** The crop's name, in the label line. */
  name: boolean;
  /** The cultivar, in the label line, when it differs from the name. */
  variety: boolean;
  /** No longer printed (photos carry no stage); kept so saved styles still read. */
  stage: boolean;
  /** The photograph's date, in the label line (in Before & after, the band's two dates). */
  date: boolean;
  /** The gardener's own line. */
  words: boolean;
  /** Their social handle, top left. */
  handle: boolean;
  /** A location's share: on a plant's photo, the location it grows in. */
  place?: boolean;
  /** A location's share: on its cover, how many plants grow there. */
  count?: boolean;
}

/**
 * What one photograph says about itself, in a share drawn from more than one
 * subject: a location's, whose cover and own photos are the location's and
 * whose plants' photos are each their plant's. Its own switches, name and
 * variety, and the extra pieces a location adds.
 */
export interface PhotoMeta {
  /** What this photograph prints. */
  elements: ShareElements;
  /** The name it prints: its plant's crop, or the location's name. */
  name: string;
  /** The variety it prints: its plant's, or the location's name again (and so left out). */
  variety: string;
  /** The location a plant's photo grows in, printed when elements.place. */
  place?: string | null;
  /** "4 plants", printed on a location's own pictures when elements.count. */
  count?: string | null;
}

/** What buildShareFrames needs to make the pictures: the plant, its photographs, and the sharer's choices. */
export interface ShareFrameInput {
  /** The plant: names the files, and is the subject when none is given. */
  plantObjId: number;
  /** A location's share draws from the location's photos; a plant's from the plant's. */
  subject?: Subject;
  /** The crop, e.g. Vining Cherry Tomato. */
  name: string;
  /** The cultivar, e.g. Black Cherry. */
  variety: string;
  /** Oldest first. */
  photos: PlantPhoto[];
  /** The frame's size. */
  size: ShareSize;
  /** How the photographs are arranged. */
  layout: ShareLayout;
  /** What is printed, unless photoMeta says otherwise for a photograph. */
  elements: ShareElements;
  /** What the card layout sits on. Ignored by the others. */
  cardGround?: CardGround;
  /**
   * How large the lettering is, 1 as designed. Everything printed scales
   * together — the words, the label line, the Grow mark, the handle, the
   * band across a before-and-after — so the frame keeps its proportions.
   */
  textScale?: number;
  /** For Peek with a single photograph: the one to show at its edge. */
  peekPhoto?: PlantPhoto;
  /**
   * The gardener's own line for a photograph. Not remembered between shares.
   * One line for all of them, or a line each: the dialog decides, this only
   * asks.
   */
  wordsFor: (photo: PlantPhoto) => string;
  /**
   * Where the photograph sits in its frame, 0..1 across and down, when it is
   * cropped to fit. A four-by-three photograph cut to a story loses two
   * thirds of itself, and which two thirds is the gardener's call.
   */
  focusFor: (photo: PlantPhoto) => { x: number; y: number; z?: number; a?: number };
  /**
   * Told each photograph's width over its height as it is read, so the studio can
   * refit a crop to another frame shape (B86).
   */
  onPhotoShape?: (photoId: number, aspect: number) => void;
  /**
   * The photograph in view in the dialog. Card and Peek send this one; a set
   * sends them all and ignores it.
   */
  lead?: PlantPhoto;
  /** Their handle, already tidied, and where it lives. Null when none is chosen. */
  handle: { text: string; place: LinkKey } | null;
  /**
   * The signature on the set's first picture: the gardener's own Grow
   * address ("grow.gardengameplan.com/@amy"); none draws nothing
   * (SHARE_SIGNATURE).
   */
  address?: string | null;
  /** Whether the Grow mark heads the text: false for an account the Grow settings leave it off. True when not said. */
  showMark?: boolean;
  /** Turns a photo's date into the words printed on the frame. */
  formatDate: (iso: string) => string;
  /** "41 days", or whatever the count comes to, for the before-and-after band. */
  formatSpan: (days: number) => string;
  /** Told after each finished picture, for a progress line. */
  onProgress?: (done: number, total: number) => void;
  /** Asked before each picture; true stops the set there. */
  isCancelled?: () => boolean;
  /** A location's share: what each photograph says about itself (PhotoMeta), else the share's own. */
  photoMeta?: (photo: PlantPhoto) => PhotoMeta | null;
  /**
   * A location's share: where a photo that is not the location's comes from,
   * its plant and its own id (a plant's photo goes by its id made negative).
   */
  photoSource?: (photoId: number) => { subject: Subject; photoId: number } | null;
  /**
   * Finished pictures kept between sets, by what each was made from
   * (frameKey). Given, a picture already made is used again rather than drawn.
   */
  frameCache?: Map<string, File>;
  /**
   * Everything every picture in the set shares (layout, size, what is
   * printed, the handle, the lettering, the subject), as one key. Required
   * for frameCache to be used: without it no picture is reused.
   */
  cacheKey?: string;
}

/**
 * What a photograph says about itself: its own (photoMeta), or the share's.
 *
 * @param input - The sharer's choices.
 * @param photo - The photograph.
 */
export function metaOf(input: ShareFrameInput, photo: PlantPhoto): PhotoMeta {
  return input.photoMeta?.(photo) ?? { elements: input.elements, name: input.name, variety: input.variety };
}

/** The text sizes the sharer steps through, smallest first. */
export const TEXT_SCALES = SHARE_TEXT_SCALES;

/**
 * The lettering's size at step 1, relative to the frame. Every step scales
 * from this, so raising it makes all the words larger and leaves the steps
 * as they are. It was 1, and the words read small on a phone.
 */
export const TEXT_BASE = SHARE_TEXT_BASE;

/**
 * How many files a layout produces from a given selection.
 *
 * @param layout - The layout.
 * @param chosen - How many photographs are going out.
 */
export function frameCount(layout: ShareLayout, chosen: number): number {
  if (chosen === 0) return 0;
  if (layout === 'collage') return chosen >= 2 ? chosen + 1 : chosen;
  return layout === 'ba' ? 1 : chosen;
}

/**
 * Whether a layout can be offered at all for this many photographs: Peek and
 * Before & after need a second one.
 *
 * @param layout - The layout.
 * @param chosen - How many photographs there are to choose from.
 */
export function layoutAvailable(layout: ShareLayout, chosen: number): boolean {
  return layout === 'full' || layout === 'card' ? chosen >= 1 : chosen >= 2;
}
